import { createTheme } from "@mui/material/styles"


const COLORS = {
  brand: {
    50: "#eff6ff",
    100: "#dbeafe",
    200: "#bfdbfe",
    300: "#93c5fd",
    400: "#60a5fa",
    500: "#3b82f6",
    600: "#2563eb",
    700: "#1d4ed8",
    800: "#1e40af",
    900: "#172554",
  },

  navy: {
    950: "#020617",
    900: "#0f172a",
    800: "#1e293b",
    700: "#334155",
  },

  success: {
    main: "#16a34a",
    light: "#dcfce7",
    dark: "#166534",
  },

  warning: {
    main: "#ea580c",
    light: "#ffedd5",
    dark: "#9a3412",
  },

  error: {
    main: "#dc2626",
    light: "#fee2e2",
    dark: "#991b1b",
  },

  info: {
    main: "#0284c7",
    light: "#e0f2fe",
    dark: "#075985",
  },

  background: {
    default: "#f4f7fb",
    paper: "#ffffff",
    subtle: "#f8fafc",
  },

  border: "#e2e8f0",

  text: {
    primary: "#0f172a",
    secondary: "#64748b",
    disabled: "#94a3b8",
  },
}


const rlsysTheme = createTheme({
  palette: {
    mode: "light",

    primary: {
      main: COLORS.brand[600],
      light: COLORS.brand[100],
      dark: COLORS.brand[800],
      contrastText: "#ffffff",
    },

    secondary: {
      main: "#7c3aed",
      contrastText: "#ffffff",
    },

    success: {
      main: COLORS.success.main,
    },

    warning: {
      main: COLORS.warning.main,
    },

    error: {
      main: COLORS.error.main,
    },

    info: {
      main: COLORS.info.main,
    },

    background: {
      default: COLORS.background.default,
      paper: COLORS.background.paper,
    },

    text: {
      primary: COLORS.text.primary,
      secondary: COLORS.text.secondary,
      disabled: COLORS.text.disabled,
    },

    divider: COLORS.border,
  },


  shape: {
    borderRadius: 10,
  },


  typography: {
    fontFamily: [
      "Inter",
      "Segoe UI",
      "Roboto",
      "Helvetica",
      "Arial",
      "sans-serif",
    ].join(","),

    h1: {
      fontWeight: 900,
      letterSpacing: -0.8,
    },

    h2: {
      fontWeight: 900,
      letterSpacing: -0.7,
    },

    h3: {
      fontWeight: 900,
      letterSpacing: -0.6,
    },

    h4: {
      fontWeight: 900,
      letterSpacing: -0.45,
    },

    h5: {
      fontWeight: 850,
      letterSpacing: -0.3,
    },

    h6: {
      fontWeight: 800,
    },

    button: {
      fontWeight: 800,
      textTransform: "none",
    },
  },


  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: "smooth",
        },

        body: {
          margin: 0,
          backgroundColor: COLORS.background.default,
          color: COLORS.text.primary,
        },

        "*": {
          boxSizing: "border-box",
        },

        "::selection": {
          backgroundColor: COLORS.brand[100],
          color: COLORS.brand[900],
        },
      },
    },


    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },

      styleOverrides: {
        root: {
          borderRadius: 10,
          minHeight: 40,
          fontWeight: 800,
          paddingInline: 16,
        },

        containedPrimary: {
          boxShadow: "0 6px 14px rgba(37,99,235,0.16)",
        },
      },
    },


    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },

      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },


    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${COLORS.border}`,
          borderRadius: 16,
          boxShadow: "0 6px 18px rgba(15,23,42,0.05)",
        },
      },
    },


    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },


    MuiFormControl: {
      defaultProps: {
        size: "small",
      },
    },


    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#ffffff",

          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: COLORS.brand[300],
          },

          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1.5,
          },
        },
      },
    },


    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },


    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
        },
      },
    },


    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 900,
          borderBottom: `1px solid ${COLORS.border}`,
        },
      },
    },


    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 800,
          borderRadius: 999,
        },
      },
    },


    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: COLORS.background.subtle,
          color: COLORS.text.primary,
          fontWeight: 900,
          borderColor: COLORS.border,
        },

        body: {
          borderColor: COLORS.border,
        },
      },
    },


    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
      },
    },


    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          fontWeight: 800,
          textTransform: "none",
        },
      },
    },


    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: "1px solid",
        },
      },
    },


    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
    },
  },
})


export { COLORS }
export default rlsysTheme
